// Centralized lazy loading components for better code splitting
import { createLazyComponent } from '../utils/performance';

// ============================================
// AUTH COMPONENTS (Critical - Load First)
// ============================================
export const LazyLogin = createLazyComponent(() => import('../pages/Auth/Login'));
export const LazyRegister = createLazyComponent(() => import('../pages/Auth/Register'));
export const LazyForgotPassword = createLazyComponent(() => import('../pages/Auth/ForgotPassword'));
export const LazyVerifyEmail = createLazyComponent(() => import('../pages/Auth/VerifyEmail'));
export const LazyTrial = createLazyComponent(() => import('../pages/Trial'));
export const LazyPostCheckoutOnboarding = createLazyComponent(() => import('../pages/Onboarding/PostCheckoutOnboarding'));
export const LazyHomeDashboard = createLazyComponent(() => import('../pages/Home/HomeDashboard'));

// ============================================
// MAIN ROUTE PAGES (Medium Priority)
// ============================================
export const LazyCalendar = createLazyComponent(() => import('../pages/Calendar'));
export const LazyDocuments = createLazyComponent(() => import('../pages/Documents'));
export const LazyAlerts = createLazyComponent(() => import('../pages/Alerts'));
export const LazyProfileSettings = createLazyComponent(() => import('../pages/ProfileSettings'));
export const LazyCareTeam = createLazyComponent(() => import('../pages/CareTeam/index'));
// Messages redesign — `/inbox` now routes to the new MessagesPage. The legacy
// pages/Inbox/index page is kept in the tree (still compiles) but unrouted.
export const LazyInbox = createLazyComponent(
  () => import('../components/Messages/MessagesPage'),
);
export const LazyInboxLegacy = createLazyComponent(
  () => import('../pages/Inbox/index'),
);
export const LazyPetExpenses = createLazyComponent(() => import('../pages/PetExpenses'));
export const LazyEmailDiagnostics = createLazyComponent(() => import('../pages/EmailDiagnostics'));
export const LazyInsurance = createLazyComponent(() => import('../pages/Insurance'));
export const LazyLabResultsPage = createLazyComponent(() => import('../pages/LabResultsPage'));
export const LazyNotifications = createLazyComponent(() => import('../pages/Notifications'));
export const LazyAskSuprie = createLazyComponent(() => import('../pages/Chat'));

// ============================================
// FORM COMPONENTS (Medium Priority)
// ============================================
// LazyEventForm removed — replaced by ScheduleAppointmentDialog (mounted globally
// via GlobalEventForm in App.tsx). The legacy 2700-line monolithic form
// conflated 4 event types into one screen; reminders + medications now route
// through ReminderDialog and the Care Plan page respectively.
export const LazyPetForm = createLazyComponent(() => import('../pages/Pets/PetForm'));
export const LazyPetProfilePage = createLazyComponent(() => import('../pages/Pets/PetProfilePage'));
export const LazyPetGenetics = createLazyComponent(() => import('../pages/Pets/PetGenetics'));

// ============================================
// HEALTH RECORDS PAGES (Medium Priority)
// ============================================
export const LazyHealthInsightsRedesigned = createLazyComponent(() => import('../pages/HealthInsights/HealthInsightsRedesigned'));
export const LazyLabResults = createLazyComponent(() => import('../pages/HealthRecords/LabResults'));
export const LazyVetVisits = createLazyComponent(() => import('../pages/HealthRecords/VetVisits'));
// DEPRECATED: LazyMedications - Medications now handled in Care Plan page only
// export const LazyMedications = createLazyComponent(() => import('../pages/HealthRecords/Medications'));
export const LazyVaccinations = createLazyComponent(() => import('../pages/HealthRecords/Vaccinations'));
export const LazyCarePlanPage = createLazyComponent(() => import('../pages/CarePlan/CarePlanPage'));

// ============================================
// WELLNESS PAGES - REMOVED
// ============================================

// ============================================
// CARE TEAM PAGES (Low Priority)
// ============================================
export const LazyCareTeamAdd = createLazyComponent(() => import('../pages/CareTeam/CareTeamAdd'));
export const LazyCareTeamFind = createLazyComponent(() => import('../pages/CareTeam/CareTeamFind'));

// ============================================
// EXPENSE PAGES (Low Priority)
// ============================================
export const LazyExpenseAdd = createLazyComponent(() => import('../pages/PetExpenses/ExpenseAdd'));
export const LazyExpenseReports = createLazyComponent(() => import('../pages/PetExpenses/ExpenseReports'));
export const LazyExpenseInsurance = createLazyComponent(() => import('../pages/PetExpenses/ExpenseInsurance'));

// ============================================
// HEAVY COMPONENTS (Lazy Load Only When Needed)
// ============================================
export const LazyVoiceAssistant = createLazyComponent(() => import('../components/VoiceAssistant'));
// Removed non-existent components

// ============================================
// QUICK ACTION DIALOGS (Lazy Load Only When Opened)
// ============================================
export const LazyScheduleAppointmentDialog = createLazyComponent(() => import('../components/QuickActions/ScheduleAppointmentDialog'));
export const LazyAddExpenseDialog = createLazyComponent(() => import('../components/QuickActions/AddExpenseDialog'));
export const LazySubmitInsuranceClaimDialog = createLazyComponent(() => import('../components/QuickActions/SubmitInsuranceClaimDialog'));
export const LazyUploadDocumentDialog = createLazyComponent(() => import('../components/QuickActions/UploadDocumentDialog'));
export const LazyHealthReportDialog = createLazyComponent(() => import('../components/QuickActions/HealthReportDialog'));
export const LazyMedicationReminderDialog = createLazyComponent(() => import('../components/QuickActions/MedicationReminderDialog'));
export const LazyEmergencyQuickAccessDialog = createLazyComponent(() => import('../components/QuickActions/EmergencyQuickAccessDialog'));
export const LazyRecentActivityHubDialog = createLazyComponent(() => import('../components/QuickActions/RecentActivityHubDialog'));

// ============================================
// AI/ML COMPONENTS (Heavy - Lazy Load)
// ============================================
// Note: Services are not React components, so they don't need lazy loading
// They can be imported normally when needed

// ============================================
// ONBOARDING COMPONENTS — RETIRED (Phase 4)
// The legacy LazyOnboardingWizard + EnhancedOnboarding overlay were retired
// in favor of the single canonical /trial → /onboarding flow.
// ============================================

// ============================================
// UTILITY COMPONENTS (Lazy Load)
// ============================================
export const LazyDataVisualization = createLazyComponent(() => import('../components/DataVisualization'));
// Removed non-existent EmailParsingView component

// ============================================
// ENHANCED LAZY LOADING WITH RETRY
// ============================================
export const LazyVoiceAssistantWithRetry = createLazyComponent(() => import('../components/VoiceAssistant'));

// ============================================
// CHUNK GROUPING FOR OPTIMAL LOADING
// ============================================

// Critical chunks (load immediately)
export const criticalChunks = [
  LazyLogin,
  LazyRegister,
  LazyHealthInsightsRedesigned
];

// Secondary chunks (load after critical)
export const secondaryChunks = [
  LazyCalendar,
  LazyHealthInsightsRedesigned
];

// Tertiary chunks (load on demand)
export const tertiaryChunks = [
  LazyPetExpenses,
  LazyInsurance,
  LazyCareTeam,
  LazyVoiceAssistant
];

// Heavy chunks (load only when needed)
export const heavyChunks = [
  // Removed non-existent components
];
